import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { mockApi, startRealtimeSimulation, stopRealtimeSimulation } from '../services/mockApi';

const AppContext = createContext();

// Action types
const ACTIONS = {
  SET_VEHICLES: 'SET_VEHICLES',
  SET_CURRENT_READINGS: 'SET_CURRENT_READINGS',
  UPDATE_READING: 'UPDATE_READING',
  SET_SETTINGS: 'SET_SETTINGS',
  UPDATE_SETTING: 'UPDATE_SETTING',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
};

// Initial state
const initialState = {
  vehicles: [],
  currentReadings: {},
  settings: {
    theme: 'light',
    temperatureUnit: 'C',
    alertSensitivity: 'medium',
    soundEnabled: true,
    emailAlerts: false,
    email: '',
  },
  loading: true,
  error: null,
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_VEHICLES:
      return { ...state, vehicles: action.payload };
      
    case ACTIONS.SET_CURRENT_READINGS:
      return { ...state, currentReadings: action.payload };
      
    case ACTIONS.UPDATE_READING:
      return {
        ...state,
        currentReadings: {
          ...state.currentReadings,
          [action.payload.vehicleId]: action.payload.reading,
        },
      };
      
    case ACTIONS.SET_SETTINGS:
      return { ...state, settings: action.payload };
      
    case ACTIONS.UPDATE_SETTING:
      return {
        ...state,
        settings: {
          ...state.settings,
          [action.payload.key]: action.payload.value,
        },
      };
      
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
      
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };
      
    default:
      return state;
  }
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  
  // Load initial data
  const loadInitialData = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Load vehicles
      const { data: vehicles, error: vehiclesError } = await mockApi.getVehicles();
      if (vehiclesError) throw new Error(vehiclesError);
      dispatch({ type: ACTIONS.SET_VEHICLES, payload: vehicles });
      
      // Load current readings
      const { data: readings, error: readingsError } = await mockApi.getAllCurrentReadings();
      if (readingsError) throw new Error(readingsError);
      dispatch({ type: ACTIONS.SET_CURRENT_READINGS, payload: readings });
      
      // Load settings
      const { data: settings, error: settingsError } = await mockApi.getSettings();
      if (settingsError) throw new Error(settingsError);
      dispatch({ type: ACTIONS.SET_SETTINGS, payload: settings });
      
      dispatch({ type: ACTIONS.SET_ERROR, payload: null });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, []);
  
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);
  
  // Real-time updates
  useEffect(() => {
    const intervalId = startRealtimeSimulation(10000); // Update every 10 seconds
    
    // Update current readings periodically
    const updateInterval = setInterval(async () => {
      const { data: readings } = await mockApi.getAllCurrentReadings();
      if (readings) {
        dispatch({ type: ACTIONS.SET_CURRENT_READINGS, payload: readings });
      }
    }, 10000);
    
    return () => {
      stopRealtimeSimulation(intervalId);
      clearInterval(updateInterval);
    };
  }, []);
  
  // Update a single reading
  const updateReading = useCallback((vehicleId, reading) => {
    dispatch({
      type: ACTIONS.UPDATE_READING,
      payload: { vehicleId, reading },
    });
  }, []);
  
  // Update settings
  const updateSettings = useCallback(async (newSettings) => {
    const { data, error } = await mockApi.updateSettings(newSettings);
    
    if (data && !error) {
      dispatch({ type: ACTIONS.SET_SETTINGS, payload: data });
    }
    
    return { data, error };
  }, []);
  
  // Update single setting
  const updateSetting = useCallback(async (key, value) => {
    dispatch({
      type: ACTIONS.UPDATE_SETTING,
      payload: { key, value },
    });
    
    // Save to API
    await mockApi.updateSettings({ [key]: value });
  }, []);
  
  const value = {
    ...state,
    updateReading,
    updateSettings,
    updateSetting,
    refreshData: loadInitialData,
  };
  
  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

